import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Paper,
  Alert,
  Grid,
  TextField,
  Button,
  Chip,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Laptop as LaptopIcon,
  Mouse as MouseIcon,
  Keyboard as KeyboardIcon,
  Monitor as MonitorIcon,
  Headphones as HeadphonesIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';

const ITAssetsHandover = ({ 
  selectedEmployee, 
  onStepComplete, 
  onStepIncomplete, 
  isCompleted,
  employeeProgress 
}) => {
  const { getAuthHeaders } = useAuth();
  const [assets, setAssets] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newAsset, setNewAsset] = useState({ name: '' });

  useEffect(() => {
    if (selectedEmployee) {
      fetchAssets();
    }
  }, [selectedEmployee]);

  const fetchAssets = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${API_URL}/it-assets/assets/${selectedEmployee.employeeId}`,
        { headers: getAuthHeaders() }
      );
      setAssets(response.data.data);
    } catch (err) {
      setError('Failed to fetch assets data');
      console.error('Error fetching assets:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAssetChange = async (assetType, provided) => {
    try {
      const response = await axios.put(
        `${API_URL}/it-assets/assets/${selectedEmployee.employeeId}/${assetType}`,
        { provided },
        { headers: getAuthHeaders() }
      );
      setAssets(response.data.data);
    } catch (err) {
      setError('Failed to update asset');
      console.error('Error updating asset:', err);
    }
  };

  const handleAddOtherAsset = async () => {
    if (!newAsset.name.trim()) return;
    
    try {
      const response = await axios.put(
        `${API_URL}/it-assets/assets/${selectedEmployee.employeeId}/otherAssets`,
        { 
          action: 'add',
          name: newAsset.name
        },
        { headers: getAuthHeaders() }
      );
      setAssets(response.data.data);
      setNewAsset({ name: '' });
    } catch (err) {
      setError('Failed to add asset');
      console.error('Error adding asset:', err);
    }
  };

  const handleDeleteOtherAsset = async (assetId) => {
    try {
      const response = await axios.put(
        `${API_URL}/it-assets/assets/${selectedEmployee.employeeId}/otherAssets`,
        { 
          action: 'delete',
          assetId
        },
        { headers: getAuthHeaders() }
      );
      setAssets(response.data.data);
    } catch (err) {
      setError('Failed to delete asset');
      console.error('Error deleting asset:', err);
    }
  };

  const handleOtherAssetChange = async (assetId, provided) => {
    try {
      const response = await axios.put(
        `${API_URL}/it-assets/assets/${selectedEmployee.employeeId}/otherAssets`,
        { 
          action: 'update',
          assetId,
          provided
        },
        { headers: getAuthHeaders() }
      );
      setAssets(response.data.data);
    } catch (err) {
      setError('Failed to update asset');
      console.error('Error updating asset:', err);
    }
  };

  if (!selectedEmployee) {
    return (
      <Alert severity="info">
        Please select an employee first to manage their IT assets handover.
      </Alert>
    );
  }

  const assetItems = [
    { key: 'laptop', label: 'Laptop', icon: <LaptopIcon /> },
    { key: 'mouse', label: 'Mouse', icon: <MouseIcon /> },
    { key: 'keyboard', label: 'Keyboard', icon: <KeyboardIcon /> },
    { key: 'monitor', label: 'Monitor', icon: <MonitorIcon /> },
    { key: 'headset', label: 'Headset', icon: <HeadphonesIcon /> },
  ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        IT Assets Handover Management
      </Typography>
      
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="body1" sx={{ mb: 3 }}>
          This step involves allocating laptop, phone, and other IT equipment to the selected employee.
        </Typography>
        
        {loading && (
          <Typography variant="body2" color="text.secondary">
            Loading assets data...
          </Typography>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {assets && (
          <Box>
            <Grid container spacing={2}>
              {assetItems.map((item) => (
                <Grid item xs={12} md={6} key={item.key}>
                  <Box sx={{ 
                    border: '1px solid #e0e0e0', 
                    borderRadius: 1, 
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    {item.icon}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={assets.assets[item.key]?.provided || false}
                          onChange={(e) => handleAssetChange(item.key, e.target.checked)}
                          color="primary"
                        />
                      }
                      label={item.label}
                      sx={{ flexGrow: 1 }}
                    />
                    {assets.assets[item.key]?.provided && (
                      <Chip 
                        label="Provided" 
                        size="small" 
                        color="success"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 3 }} />
            
            {/* Other Assets */}
            <Typography variant="h6" gutterBottom>
              Other Assets
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Asset Name"
                    value={newAsset.name}
                    onChange={(e) => setNewAsset({ name: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddOtherAsset}
                    disabled={!newAsset.name.trim()}
                  >
                    Add Asset
                  </Button>
                </Grid>
              </Grid>
            </Box>

            {assets.assets.otherAssets && assets.assets.otherAssets.length > 0 && (
              <Box>
                {assets.assets.otherAssets.map((asset) => (
                  <Box key={asset._id} sx={{ 
                    border: '1px solid #e0e0e0', 
                    borderRadius: 1, 
                    p: 2, 
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={asset.provided || false}
                          onChange={(e) => handleOtherAssetChange(asset._id, e.target.checked)}
                          color="primary"
                        />
                      }
                      label={asset.name}
                      sx={{ flexGrow: 1 }}
                    />
                    {asset.provided && (
                      <Chip 
                        label="Provided" 
                        size="small" 
                        color="success"
                        variant="outlined"
                      />
                    )}
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteOtherAsset(asset._id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            {/* Summary */}
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Assets Summary
              </Typography>
              <Typography variant="body2">
                Provided: {assets.providedAssets} / {assets.totalAssets} assets
              </Typography>
            </Box>

            {/* Mark as Complete */}
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isCompleted}
                    onChange={(event) => {
                      if (event.target.checked) {
                        onStepComplete();
                      } else {
                        onStepIncomplete();
                      }
                    }}
                    color="primary"
                  />
                }
                label="Mark as Complete"
              />
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ITAssetsHandover; 